/**
 * Palette d'accent du `LiveCoachSignal`.
 *
 * Toutes les couleurs sont exprimées comme des chaînes CSS valides afin de
 * pouvoir être assignées à des variables CSS (`--coach-stroke`, etc.) et
 * transitionnées par le compositeur GPU.
 *
 * Les couleurs sont volontairement dérivées des HSL tokens du design system
 * (cf. `client/src/index.css` et `client/tailwind.config.js`) pour rester
 * cohérentes avec la marque et être gratuitement compatibles avec le mode
 * sombre.
 */
export type CoachAccent = {
    /** Couleur principale des courbes (stroke des paths SVG). */
    stroke: string;
    /** Couleur du halo / glow (typiquement plus claire et semi-transparente). */
    glow: string;
    /** Couleur du liseré subtil (border) du conteneur. */
    ring: string;
};

/**
 * Permet aux appelants d'utiliser les clés bien connues sans perdre la
 * possibilité d'envoyer un statut maison (`(string & {})` désactive
 * l'autocomplétion mais autorise n'importe quelle string).
 *
 * `not_connected` = pont avec l'app desktop rompu (= utilisateur doit
 * lancer son app). On garde `connecting` comme alias visuel : même palette
 * grise et mêmes paramètres d'onde, c'est traité comme un état "muet".
 * `completed` = appel terminé, recap visible — onde calme et palette
 * douce pour signaler "tu peux relire, plus rien n'arrive en live".
 */
export type CoachStatusKey = "not_connected" | "connecting" | "idle" | "on_call" | "completed" | (string & {});

/**
 * Helper pour fabriquer une couleur HSL avec alpha optionnel, à partir des
 * tokens HSL bruts du design system (ex: "212 88% 59%").
 */
function hslToken(token: string, alpha?: number): string {
    if (typeof alpha === "number") return `hsl(${token} / ${alpha})`;
    return `hsl(${token})`;
}

const TOKENS = {
    azureBlue: "212 88% 59%",
    denimBlue: "230 52% 52%",
    indigoSoft: "230 60% 64%",
    slateMuted: "220 14% 60%",
    shamrockGreen: "156 60% 42%",
    strawYellow: "40 97% 55%",
    utOrange: "31 100% 55%",
    pantoneRed: "355 75% 56%"
} as const;

const MUTED_GREY_ACCENT: CoachAccent = {
    stroke: hslToken(TOKENS.slateMuted),
    glow: hslToken(TOKENS.slateMuted, 0.35),
    ring: hslToken(TOKENS.slateMuted, 0.25)
};

const STATUS_PALETTE: Record<string, CoachAccent> = {
    not_connected: MUTED_GREY_ACCENT,
    connecting: MUTED_GREY_ACCENT,
    idle: {
        stroke: hslToken(TOKENS.indigoSoft),
        glow: hslToken(TOKENS.indigoSoft, 0.35),
        ring: hslToken(TOKENS.indigoSoft, 0.22)
    },
    on_call: {
        stroke: hslToken(TOKENS.shamrockGreen),
        glow: hslToken(TOKENS.shamrockGreen, 0.45),
        ring: hslToken(TOKENS.shamrockGreen, 0.28)
    },
    /*
     * Recap post-appel : vert atténué (variante "ralentie" du
     * `on_call`). Signale "tu es revenu d'un appel, plus de live mais le
     * contexte reste consultable" sans flasher comme un appel actif.
     */
    completed: {
        stroke: hslToken(TOKENS.shamrockGreen, 0.7),
        glow: hslToken(TOKENS.shamrockGreen, 0.22),
        ring: hslToken(TOKENS.shamrockGreen, 0.18)
    }
};

/**
 * Palettes par "famille sémantique" — partagées entre plusieurs clés (signal
 * codes, trigger types legacy, etc.) pour rester cohérent dans toute l'UI.
 */
const OBJECTION_ACCENT: CoachAccent = {
    stroke: hslToken(TOKENS.utOrange),
    glow: hslToken(TOKENS.utOrange, 0.55),
    ring: hslToken(TOKENS.utOrange, 0.32)
};
const MILESTONE_POSITIVE_ACCENT: CoachAccent = {
    stroke: hslToken(TOKENS.shamrockGreen),
    glow: hslToken(TOKENS.shamrockGreen, 0.5),
    ring: hslToken(TOKENS.shamrockGreen, 0.3)
};
const MILESTONE_NEUTRAL_ACCENT: CoachAccent = {
    stroke: hslToken(TOKENS.azureBlue),
    glow: hslToken(TOKENS.azureBlue, 0.55),
    ring: hslToken(TOKENS.azureBlue, 0.32)
};
const RISK_BEHAVIOR_ACCENT: CoachAccent = {
    stroke: hslToken(TOKENS.strawYellow),
    glow: hslToken(TOKENS.strawYellow, 0.55),
    ring: hslToken(TOKENS.strawYellow, 0.32)
};

/**
 * Mapping signal_code → palette. Les codes sont ceux configurés côté backend
 * via l'admin SPA (Signal.code) — ajouter ici quand un nouveau signal est
 * créé pour qu'il bénéficie d'une couleur dédiée plutôt que de l'accent
 * neutre par défaut.
 *
 * Le mapping est aussi utilisé pour les trigger_types V1 legacy
 * (`objection_match`, `assist.suggestion`...) afin de garder une transition
 * douce le temps que tous les events portent le nouveau `detected_signal`.
 */
const EVENT_PALETTE: Record<string, CoachAccent> = {
    // Signal types (familles) — mirror Signal.type côté backend (cf.
    // `apps/call_analysis/models.py::SIGNAL_TYPE_CHOICES`). Exposé pour
    // que les consommateurs (dev simulator, futurs widgets) puissent
    // dériver une couleur directement du type sémantique sans avoir à
    // connaître un code spécifique.
    objection: OBJECTION_ACCENT,
    behavior: RISK_BEHAVIOR_ACCENT,
    risk: RISK_BEHAVIOR_ACCENT,
    milestone: MILESTONE_POSITIVE_ACCENT,
    context: MILESTONE_NEUTRAL_ACCENT,

    // Signal codes V2 (apps/call_analysis) ----------------------------------
    price_objection: OBJECTION_ACCENT,
    already_done_objection: OBJECTION_ACCENT,
    need_to_think_objection: OBJECTION_ACCENT,
    pitch_too_early: RISK_BEHAVIOR_ACCENT,
    sales_too_verbose: RISK_BEHAVIOR_ACCENT,
    call_context_confirmed: MILESTONE_NEUTRAL_ACCENT,
    profession_identified: MILESTONE_NEUTRAL_ACCENT,
    eligibility_clarified: MILESTONE_NEUTRAL_ACCENT,
    need_identified: MILESTONE_NEUTRAL_ACCENT,
    urgency_identified: MILESTONE_NEUTRAL_ACCENT,
    closing_opportunity: MILESTONE_POSITIVE_ACCENT,
    next_step_secured: MILESTONE_POSITIVE_ACCENT,

    // Layer 2 Conductor — directive stratégique (pas un signal). Indigo
    // doux pour se distinguer nettement des objections (orange) et des
    // milestones (bleu/vert), tout en restant compatible avec le glow
    // animé de la card en tête de liste.
    layer_2_conductor: {
        stroke: hslToken(TOKENS.pantoneRed),
        glow: hslToken(TOKENS.pantoneRed, 0.45),
        ring: hslToken(TOKENS.pantoneRed, 0.25)
    },

    // Trigger types / events legacy V1 — back-compat ------------------------
    "assist.suggestion": {
        stroke: hslToken(TOKENS.azureBlue),
        glow: hslToken(TOKENS.azureBlue, 0.55),
        ring: hslToken(TOKENS.azureBlue, 0.32)
    },
    "coach.suggestion": {
        stroke: hslToken(TOKENS.azureBlue),
        glow: hslToken(TOKENS.azureBlue, 0.55),
        ring: hslToken(TOKENS.azureBlue, 0.32)
    },
    objection_match: OBJECTION_ACCENT,
    prospect_question_or_objection: RISK_BEHAVIOR_ACCENT,
    question: {
        stroke: hslToken(TOKENS.strawYellow),
        glow: hslToken(TOKENS.strawYellow, 0.5),
        ring: hslToken(TOKENS.strawYellow, 0.3)
    },
    "call.start": MILESTONE_POSITIVE_ACCENT,
    "call.end": {
        stroke: hslToken(TOKENS.slateMuted),
        glow: hslToken(TOKENS.slateMuted, 0.45),
        ring: hslToken(TOKENS.slateMuted, 0.28)
    }
};

const DEFAULT_EVENT_ACCENT: CoachAccent = {
    stroke: hslToken(TOKENS.denimBlue),
    glow: hslToken(TOKENS.denimBlue, 0.5),
    ring: hslToken(TOKENS.denimBlue, 0.3)
};

const DEFAULT_STATUS_ACCENT: CoachAccent = STATUS_PALETTE.idle;

export function resolveCoachStatusAccent(status: string): CoachAccent {
    return STATUS_PALETTE[status] ?? DEFAULT_STATUS_ACCENT;
}

/**
 * Renvoie la palette d'un type d'événement, ou `null` si on ne veut pas
 * d'accent spécifique (le composant retombera sur la palette du statut).
 *
 * Les types inconnus reçoivent une palette neutre par défaut plutôt que
 * `null` : c'est plus parlant qu'aucun feedback visuel.
 */
export function resolveCoachEventAccent(eventType?: string): CoachAccent | null {
    if (!eventType) return null;
    return EVENT_PALETTE[eventType] ?? DEFAULT_EVENT_ACCENT;
}

/**
 * Libellé `aria-label` à donner au wrapper en fonction du statut.
 * Le composant l'utilise par défaut si l'appelant ne fournit pas le sien.
 */
export function ariaLabelFromStatus(status: string, label?: string): string {
    const prefix = label ? `${label} : ` : "";
    switch (status) {
        case "not_connected":
            return `${prefix}application desktop non connectée`;
        case "connecting":
            return `${prefix}connexion en cours`;
        case "on_call":
            return `${prefix}appel en cours, écoute active`;
        case "idle":
            return `${prefix}en veille`;
        case "completed":
            return `${prefix}appel terminé, recap disponible`;
        default:
            return `${prefix}${status}`;
    }
}
